import * as actionTypes from './actionTypes';
import checkHttpError from './checkHttpError';
import responseToJson from './responseToJson';
import { attachRecaptchaToken } from './attachRecaptchaToken';
import { trackFormstackHandoff } from '../lib/configureDatadog';
import { cacheApplicationDivision } from '../lib/analyticsHelper';
import withTimeout from '../lib/withTimeout';
import Config from '../Config';

const config = new Config();

export function clearApplicationData() {
  return {
    type: actionTypes.CLEAR_APPLICATION_DATA,
  };
}

export function fetchApplicationRequest() {
  return {
    type: actionTypes.FETCH_APPLICATION_REQUEST,
  };
}

export function fetchApplicationSuccess(application) {
  return {
    type: actionTypes.FETCH_APPLICATION_SUCCESS,
    application,
  };
}

export function fetchApplicationError(error) {
  return {
    type: actionTypes.FETCH_APPLICATION_ERROR,
    error,
  };
}

export function fetchApplication(applicationId, authToken) {
  return (dispatch) => {
    dispatch(fetchApplicationRequest());

    const options = {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: authToken,
        'Content-Type': 'application/json',
        Pragma: 'no-cache',
      },
      mode: 'cors',
    };

    return fetch(`${config.apiUrl}/applications/${applicationId}`, options)
      .then(checkHttpError)
      .then(responseToJson)
      .then((json) => {
        cacheApplicationDivision(json);
        return dispatch(fetchApplicationSuccess(json));
      })
      .catch(error => dispatch(fetchApplicationError(error)));
  };
}

export function startApplicationRequest() {
  return { type: actionTypes.START_APPLICATION_REQUEST };
}

export function startApplicationError(error) {
  return { type: actionTypes.START_APPLICATION_ERROR, error };
}

export function startApplicationSuccess(applicationId) {
  return { type: actionTypes.START_APPLICATION_SUCCESS, applicationId };
}

export function startApplication(userId, authToken) {
  return (dispatch) => {
    dispatch(startApplicationRequest());
    dispatch(clearApplicationData());
    return attachRecaptchaToken({ userId }, 'application')
      .then((data) => {
        const options = {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            Authorization: authToken,
            'Content-Type': 'application/json',
          },
          mode: 'cors',
          body: JSON.stringify(data),
        };
        return fetch(`${config.apiUrl}/users/${userId}/createApplication`, options);
      })
      .then(checkHttpError)
      .then(responseToJson)
      .then(json => dispatch(startApplicationSuccess(json)))
      .catch((error) => {
        dispatch(startApplicationError(error));
        return Promise.reject(error);
      });
  };
}

export function submitEntityApplicationRequest() {
  return { type: actionTypes.SUBMIT_ENTITY_APPLICATION_REQUEST };
}

export function submitEntityApplicationError(error) {
  return { type: actionTypes.SUBMIT_ENTITY_APPLICATION_ERROR, error };
}

export function submitEntityApplicationSuccess(application) {
  return { type: actionTypes.SUBMIT_ENTITY_APPLICATION_SUCCESS, application };
}

export function submitEntityApplication(body, applicationId, authToken) {
  return (dispatch) => {
    dispatch(submitEntityApplicationRequest());
    const options = {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        Authorization: authToken,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
      mode: 'cors',
    };

    return fetch(
      `${config.apiUrl}/applications/${applicationId}/paperApplication`,
      options,
    )
      .then(checkHttpError)
      .then(responseToJson)
      .then(json => dispatch(submitEntityApplicationSuccess(json)))
      .catch((error) => {
        dispatch(submitEntityApplicationError(error));
        return Promise.reject(error);
      });
  };
}

export const FORMSTACK_HANDOFF_TIMEOUT_MS = 15000;

export function formstackHandoffRequest() {
  return { type: actionTypes.FORMSTACK_HANDOFF_REQUEST };
}

export function formstackHandoffError(error) {
  return { type: actionTypes.FORMSTACK_HANDOFF_ERROR, error };
}

export function formstackHandoffSuccess(application) {
  return { type: actionTypes.FORMSTACK_HANDOFF_SUCCESS, application };
}

// Shared with formstackPrefillActions: both Formstack-facing calls are on the applicant's
// critical path, so both bound the wait rather than hanging. See src/lib/withTimeout.js.

export function formstackHandoff(applicationId, authToken, timeoutMs = FORMSTACK_HANDOFF_TIMEOUT_MS) {
  return (dispatch) => {
    dispatch(formstackHandoffRequest());
    const options = {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        Authorization: authToken,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}),
      mode: 'cors',
    };

    const startedAt = Date.now();
    // 'implicit' is set before the throw so the catch can tell a 2xx-without-an-id apart from an
    // outright rejection; without it both would land in RUM as the same explicit failure. A
    // timeout is classified from the error itself, since withTimeout rejects out of band.
    let errorKind = 'explicit';

    return withTimeout(
      fetch(`${config.apiUrl}/applications/${applicationId}/formstackHandoff`, options)
        .then(checkHttpError)
        .then(responseToJson),
      timeoutMs,
    )
      .then((json) => {
        // A 2xx without an Application ID means no correlated record came back: treat it as
        // a failure rather than routing the applicant into a record-less embedded form.
        if (!json || !json.id) {
          errorKind = 'implicit';
          throw new Error('The application hand-off did not return an Application ID.');
        }
        trackFormstackHandoff({
          outcome: 'success',
          durationMs: Date.now() - startedAt,
          applicationId,
        });
        return dispatch(formstackHandoffSuccess(json));
      })
      .catch((error) => {
        trackFormstackHandoff({
          outcome: 'failure',
          durationMs: Date.now() - startedAt,
          errorKind: error && error.isTimeout ? 'timeout' : errorKind,
          applicationId,
        });
        dispatch(formstackHandoffError(error));
        return Promise.reject(error);
      });
  };
}

// Well under the hand-off's 15s: this runs after Formstack has already accepted the form, while
// the applicant waits on the embed for the confirmation page, and its failure is not shown to them.
export const FORMSTACK_SUBMISSION_TIMEOUT_MS = 8000;

export function formstackSubmissionRequest() {
  return { type: actionTypes.FORMSTACK_SUBMISSION_REQUEST };
}

export function formstackSubmissionError(error) {
  return { type: actionTypes.FORMSTACK_SUBMISSION_ERROR, error };
}

export function formstackSubmissionSuccess(application) {
  return { type: actionTypes.FORMSTACK_SUBMISSION_SUCCESS, application };
}

// Records a completed Formstack submission so ellis moves the application to `submitted`, which
// takes it off the applicant's pending-applications list. The response is deliberately not
// written to the `application` slice: EntityApplicationPage picks the embed or the PDF from that
// slice at render time, so replacing it while the embed waits on this call could swap the page
// out from under it. The confirmation route re-fetches the application on mount anyway.
export function formstackSubmission(applicationId, authToken, timeoutMs = FORMSTACK_SUBMISSION_TIMEOUT_MS) {
  return (dispatch) => {
    dispatch(formstackSubmissionRequest());
    const options = {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        Authorization: authToken,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}),
      mode: 'cors',
    };

    return withTimeout(
      fetch(`${config.apiUrl}/applications/${applicationId}/formstackSubmission`, options)
        .then(checkHttpError)
        .then(responseToJson),
      timeoutMs,
    )
      .then(json => dispatch(formstackSubmissionSuccess(json)))
      .catch((error) => {
        // Status and timeout tag only, as toSafeError does for the prefill: checkHttpError
        // attaches the whole Response, and this error travels on to Redux and RUM.
        const safeError = new Error((error && error.message) || 'The Formstack submission was not recorded.');
        if (error && error.response) safeError.status = error.response.status;
        if (error && error.isTimeout) safeError.isTimeout = true;
        dispatch(formstackSubmissionError(safeError));
        return Promise.reject(safeError);
      });
  };
}

export function arkoseTokenValidationRequest() {
  return { type: actionTypes.ARKOSE_TOKEN_VALIDATION_REQUEST };
}

export function arkoseTokenValidationError(error) {
  return { type: actionTypes.ARKOSE_TOKEN_VALIDATION_ERROR, error };
}

export function arkoseTokenValidationSuccess(json) {
  return { type: actionTypes.ARKOSE_TOKEN_VALIDATION_SUCCESS, json };
}

export function arkoseTokenValidation(email, data) {
  const body = {
    email,
    data: JSON.stringify(data),
  };
  return (dispatch) => {
    dispatch(arkoseTokenValidationRequest());
    const options = {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      mode: 'cors',
      body: JSON.stringify(body),
    };
    return fetch(`${config.apiUrl}/applications/arkoseValidate`, options)
      .then(checkHttpError)
      .then(responseToJson)
      .then(json => dispatch(arkoseTokenValidationSuccess(json)))
      .catch((error) => {
        dispatch(arkoseTokenValidationError(error));
        return Promise.reject(error);
      });
  };
}
